import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  getAllBookings,
  deleteBooking,
  updateBooking
} from '../store/slices/bookingSlice'
import { fetchSocieties } from '../store/slices/societySlice'
import { useSidebarContext } from '../scenes/global/sidebar/SidebarContext'
import {
  FileText,
  Printer,
  MapPin,
  ChevronDown,
  Filter,
  Search,
  Trash2,
  Edit,
  X,
  Lock,
  ChevronRight,
  ChevronLeft,
  Download
} from 'lucide-react'
import printJS from 'print-js'
import 'print-js/dist/print.css'

import Logo from '../assets/Logo/Logo.png'
import ReactDatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import {
  formatExportData,
  exportToExcel,
  filterBookingsByDateRange,
  getDateRanges
} from './formatExportData'
import Swal from 'sweetalert2'
import { useReactToPrint } from 'react-to-print'

// Helper function to convert decimal years to years and months
const convertYearsToYearsAndMonths = years => {
  if (!years || years === 0) return { years: 0, months: 0 }

  const totalMonths = Math.round(years * 12)
  const yearsPart = Math.floor(totalMonths / 12)
  const monthsPart = totalMonths % 12

  return { years: yearsPart, months: monthsPart }
}

// Helper function to format installment duration for display
const formatInstallmentDuration = years => {
  if (!years || years === 0) return 'N/A'

  const { years: yearsPart, months: monthsPart } =
    convertYearsToYearsAndMonths(years)

  if (yearsPart === 0) {
    return `${monthsPart} Month${monthsPart !== 1 ? 's' : ''}`
  } else if (monthsPart === 0) {
    return `${yearsPart} Year${yearsPart !== 1 ? 's' : ''}`
  } else {
    return `${yearsPart} Year${yearsPart !== 1 ? 's' : ''} ${monthsPart} Month${
      monthsPart !== 1 ? 's' : ''
    }`
  }
}

const FullPaymentBookings = () => {
  const dispatch = useDispatch()
  const { bookings } = useSelector(state => state.booking)
  // console.log("Redux bookings:", bookings);

  const { activeSociety, theme, gradient } = useSidebarContext()
  const { societies = [] } = useSelector(state => state.societies)
  const activeUser = useSelector(state => state.auth.activeUser)
  const [allBookings, setAllBookings] = useState([])
  const [isFetchingAll, setIsFetchingAll] = useState(false)

  // State management
  const [searchTerm, setSearchTerm] = useState('')
  const [sortOption, setSortOption] = useState('latest')
  const [currentPage, setCurrentPage] = useState(1)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [selectedBooking, setSelectedBooking] = useState(null)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [editFormData, setEditFormData] = useState({})
  const [filteredUsers, setFilteredUsers] = useState([])
  const [plotSearchTerm, setPlotSearchTerm] = useState('')
  const rowsPerPage = 8
  const isEmployee = activeUser?.role === 'Employee'
  const printRef = useRef()
  const [printData, setPrintData] = useState(null)
  const componentRef = useRef(null)
  const [showFilters, setShowFilters] = useState(false)
  const [exportDialogOpen, setExportDialogOpen] = useState(false)
  const [exportStartDate, setExportStartDate] = useState(null)
  const [exportEndDate, setExportEndDate] = useState(null)
  const [exportPreset, setExportPreset] = useState('custom')
  const [exportType, setExportType] = useState('filtered')

  useEffect(() => {
    // dispatch(getAllBookings())
    dispatch(fetchSocieties())
  }, [dispatch])
  useEffect(() => {
    const fetchAllBookings = async () => {
      setIsFetchingAll(true)
      try {
        let allFetchedBookings = []
        let currentPage = 1
        let totalPages = 1

        const firstPageResult = await dispatch(
          getAllBookings({
            page: 1,
            limit: 100,
            payment_mode: 'Full',
            societyId: activeSociety?._id // Add society filter here
          })
        ).unwrap()

        allFetchedBookings = [...firstPageResult.bookings]
        totalPages = firstPageResult.totalPages

        if (totalPages > 1) {
          const pagePromises = []
          for (let page = 2; page <= totalPages; page++) {
            pagePromises.push(
              dispatch(
                getAllBookings({
                  page,
                  limit: 100,
                  payment_mode: 'Full',
                  societyId: activeSociety?._id // Add here too
                })
              ).unwrap()
            )
          }

          const remainingPages = await Promise.all(pagePromises)
          remainingPages.forEach(result => {
            allFetchedBookings = [...allFetchedBookings, ...result.bookings]
          })
        }

        setAllBookings(allFetchedBookings)
      } catch (error) {
        console.error('Error fetching all bookings:', error)
      } finally {
        setIsFetchingAll(false)
      }
    }

    fetchAllBookings()
    dispatch(fetchSocieties())
  }, [dispatch, activeSociety?._id])

  useEffect(() => {
    if (activeSociety?.users) {
      setFilteredUsers(activeSociety.users)
    }
  }, [activeSociety])

  const filteredAndSortedBookings = useMemo(() => {
    let result = [...allBookings] // Already filtered by backend

    // Only client-side search and sorting
    if (searchTerm) {
      result = result.filter(booking => {
        const bookingNumber =
          booking.booking_number?.toString().toLowerCase() || ''
        const username = booking?.customer_id?.name?.toLowerCase() || 'unknown'
        return (
          bookingNumber.includes(searchTerm.toLowerCase()) ||
          username.includes(searchTerm.toLowerCase())
        )
      })
    }

    if (plotSearchTerm) {
      result = result.filter(booking => {
        const plotNumber =
          booking.plot_id?.plot_number?.toString().toLowerCase() || ''
        return plotNumber.includes(plotSearchTerm.toLowerCase())
      })
    }

    return result.sort((a, b) => {
      switch (sortOption) {
        case 'amount-asc':
          return a.total_amount - b.total_amount
        case 'amount-desc':
          return b.total_amount - a.total_amount
        case 'latest':
        default:
          return new Date(b.createdAt) - new Date(a.createdAt)
      }
    })
  }, [allBookings, searchTerm, plotSearchTerm, sortOption])

  // Calculate total pages
  const totalPages = Math.ceil(filteredAndSortedBookings.length / rowsPerPage)

  // Get current page's bookings
  const paginatedBookings = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage
    const endIndex = startIndex + rowsPerPage
    return filteredAndSortedBookings.slice(startIndex, endIndex)
  }, [filteredAndSortedBookings, currentPage, rowsPerPage])

  // Navigation functions
  const handlePreviousPage = () => {
    setCurrentPage(prev => Math.max(1, prev - 1))
  }

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(totalPages, prev + 1))
  }

  //   const handlePrint = (booking) => {
  //     // console.log('Printing booking:', booking)
  //     const customer = booking.customer_id;
  //     const societyDetails =
  //       booking.plot_id && booking.plot_id.society_id
  //         ? getSocietyDetails(booking.plot_id.society_id)
  //         : {
  //             name: "Housing City",
  //             location: "Main Branch, City Center",
  //             image: Logo,
  //           };

  //     if (!customer || !customer.contactInfo) {
  //       console.error("Customer details are missing.");
  //       return;
  //     }

  //     const printContent = `
  // <!DOCTYPE html>
  // <html lang="en">
  // <head>
  //     <meta charset="UTF-8">
  //     <meta name="viewport" content="width=device-width, initial-scale=1.0">
  //     <title>Booking Confirmation</title>
  //     <style>
  //         @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700&family=Inter:wght@300;400;500;600;700&display=swap');

  //         * {
  //             box-sizing: border-box;
  //             margin: 0;
  //             padding: 0;
  //         }

  //         body {
  //             font-family: 'Inter', Arial, sans-serif;
  //             line-height: 1.4;
  //             color: #1a1a1a;
  //             margin: 0;
  //             padding: 0;
  //             background: white;
  //         }

  //         .container {
  //             max-width: 100%;
  //             margin: 0;
  //             background: white;
  //         }

  //         .header {
  //             background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  //             padding: 10px 15px;
  //             color: white;
  //             position: relative;
  //             overflow: hidden;
  //         }

  //         .header::before {
  //             content: '';
  //             position: absolute;
  //             top: -50%;
  //             right: -50%;
  //             width: 200%;
  //             height: 200%;
  //             background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
  //         }

  //         .header-content {
  //             position: relative;
  //             z-index: 1;
  //             display: flex;
  //             justify-content: space-between;
  //             align-items: center;
  //         }

  //         .header-logo {
  //             display: flex;
  //             align-items: center;
  //             gap: 10px;
  //         }

  //         .header-logo img {
  //             width: 45px;
  //             height: 45px;
  //             border-radius: 6px;
  //             border: 2px solid rgba(255,255,255,0.3);
  //             object-fit: cover;
  //         }

  //         .header-logo-text h1 {
  //             margin: 0;
  //             font-family: 'Playfair Display', serif;
  //             font-size: 20px;
  //             font-weight: 700;
  //             color: white;
  //         }

  //         .header-logo-text h4 {
  //             margin: 2px 0 0;
  //             font-size: 10px;
  //             color: rgba(255,255,255,0.9);
  //             font-weight: 300;
  //         }

  //         .header-booking-info {
  //             text-align: right;
  //             background: rgba(255,255,255,0.15);
  //             padding: 6px 12px;
  //             border-radius: 5px;
  //             backdrop-filter: blur(10px);
  //         }

  //         .header-booking-info p {
  //             margin: 2px 0;
  //             font-size: 10px;
  //             font-weight: 500;
  //         }

  //         .header-booking-info strong {
  //             font-weight: 600;
  //         }

  //         .content-wrapper {
  //             padding: 15px 20px;
  //         }

  //         .title-section {
  //             text-align: center;
  //             margin-bottom: 12px;
  //         }

  //         .title {
  //             font-family: 'Playfair Display', serif;
  //             font-size: 18px;
  //             font-weight: 700;
  //             color: #667eea;
  //             margin-bottom: 4px;
  //             text-transform: uppercase;
  //             letter-spacing: 0.5px;
  //         }

  //         .subtitle {
  //             font-size: 10px;
  //             color: #666;
  //             font-weight: 500;
  //             padding: 4px 12px;
  //             background: #f0f0f0;
  //             display: inline-block;
  //             border-radius: 12px;
  //         }

  //         .badge {
  //             display: inline-block;
  //             background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
  //             color: white;
  //             padding: 4px 12px;
  //             border-radius: 12px;
  //             font-size: 9px;
  //             font-weight: 600;
  //             margin-top: 5px;
  //         }

  //         .details-section {
  //             display: grid;
  //             grid-template-columns: 1fr 1fr;
  //             gap: 12px;
  //             margin-bottom: 12px;
  //         }

  //         .details-column {
  //             background: linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%);
  //             border: 2px solid #e0e7ff;
  //             border-radius: 6px;
  //             padding: 12px;
  //             position: relative;
  //             overflow: hidden;
  //         }

  //         .details-column::before {
  //             content: '';
  //             position: absolute;
  //             top: 0;
  //             left: 0;
  //             width: 3px;
  //             height: 100%;
  //             background: linear-gradient(180deg, #667eea 0%, #764ba2 100%);
  //         }

  //         .details-column h3 {
  //             margin: 0 0 8px;
  //             font-size: 13px;
  //             font-weight: 700;
  //             color: #667eea;
  //             padding-bottom: 6px;
  //             border-bottom: 2px solid #e0e7ff;
  //             display: flex;
  //             align-items: center;
  //             gap: 6px;
  //         }

  //         .details-column h3::before {
  //             content: '●';
  //             color: #764ba2;
  //             font-size: 14px;
  //         }

  //         .details-item {
  //             background: white;
  //             border-left: 2px solid #e0e7ff;
  //             border-radius: 3px;
  //             padding: 5px 8px;
  //             margin-bottom: 5px;
  //             font-size: 10px;
  //             line-height: 1.3;
  //         }

  //         .details-item strong {
  //             color: #667eea;
  //             font-weight: 600;
  //             display: inline-block;
  //             min-width: 85px;
  //         }

  //         .full-width-section {
  //             background: linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%);
  //             border: 2px solid #e0e7ff;
  //             border-radius: 6px;
  //             padding: 12px;
  //             margin-bottom: 12px;
  //             position: relative;
  //             overflow: hidden;
  //         }

  //         .full-width-section::before {
  //             content: '';
  //             position: absolute;
  //             top: 0;
  //             left: 0;
  //             width: 3px;
  //             height: 100%;
  //             background: linear-gradient(180deg, #667eea 0%, #764ba2 100%);
  //         }

  //         .full-width-section h3 {
  //             margin: 0 0 8px;
  //             font-size: 13px;
  //             font-weight: 700;
  //             color: #667eea;
  //             padding-bottom: 6px;
  //             border-bottom: 2px solid #e0e7ff;
  //             display: flex;
  //             align-items: center;
  //             gap: 6px;
  //         }

  //         .full-width-section h3::before {
  //             content: '●';
  //             color: #764ba2;
  //             font-size: 14px;
  //         }

  //         .waris-grid {
  //             display: grid;
  //             grid-template-columns: 1fr 1fr;
  //             gap: 8px;
  //         }

  //         .signature-section {
  //             display: grid;
  //             grid-template-columns: 1fr 1fr;
  //             gap: 25px;
  //             margin: 15px 0 12px;
  //             padding: 0 12px;
  //         }

  //         .signature-column {
  //             text-align: center;
  //             position: relative;
  //             padding-top: 30px;
  //         }

  //         .signature-column::before {
  //             content: '';
  //             position: absolute;
  //             top: 0;
  //             left: 0;
  //             right: 0;
  //             height: 1px;
  //             background: linear-gradient(90deg, transparent, #667eea, transparent);
  //         }

  //         .signature-column p {
  //             color: #667eea;
  //             font-weight: 600;
  //             font-size: 11px;
  //             text-transform: uppercase;
  //             letter-spacing: 0.5px;
  //         }

  //         .terms-section {
  //             background: linear-gradient(135deg, #ffeaa7 0%, #fdcb6e 100%);
  //             border: 2px solid #f39c12;
  //             border-radius: 6px;
  //             padding: 12px;
  //             position: relative;
  //         }

  //         .terms-section::before {
  //             content: '⚠';
  //             position: absolute;
  //             top: 8px;
  //             right: 8px;
  //             font-size: 25px;
  //             opacity: 0.2;
  //         }

  //         .terms-section h3 {
  //             margin: 0 0 8px;
  //             font-size: 13px;
  //             font-weight: 700;
  //             color: #d35400;
  //             padding-bottom: 6px;
  //             border-bottom: 2px solid rgba(211, 84, 0, 0.3);
  //         }

  //         .terms-section ol {
  //             padding-left: 16px;
  //             line-height: 1.4;
  //             font-size: 9px;
  //             color: #7d4f1b;
  //             margin: 0;
  //         }

  //         .terms-section li {
  //             margin-bottom: 3px;
  //             font-weight: 500;
  //         }

  //         .footer {
  //             text-align: center;
  //             padding: 12px;
  //             background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  //             color: white;
  //             margin-top: 12px;
  //         }

  //         .footer p {
  //             margin: 2px 0;
  //             font-size: 9px;
  //             opacity: 0.9;
  //         }

  //         .footer p:first-child {
  //             font-weight: 600;
  //             font-size: 10px;
  //         }

  //         .amount-highlight {
  //             background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
  //             color: white !important;
  //             padding: 8px;
  //             border-radius: 5px;
  //             font-size: 12px;
  //             font-weight: 700;
  //             text-align: center;
  //             border: none !important;
  //         }

  //         @media print {
  //             @page {
  //                 size: A4;
  //                 margin: 10mm;
  //             }

  //             body {
  //                 padding: 0;
  //                 margin: 0;
  //                 background: white;
  //                 -webkit-print-color-adjust: exact;
  //                 print-color-adjust: exact;
  //             }

  //             .container {
  //                 box-shadow: none;
  //                 border-radius: 0;
  //                 page-break-inside: avoid;
  //             }

  //             .header {
  //                 padding: 8px 12px;
  //             }

  //             .content-wrapper {
  //                 padding: 12px 15px;
  //             }

  //             .details-section,
  //             .full-width-section,
  //             .signature-section,
  //             .terms-section {
  //                 page-break-inside: avoid;
  //             }

  //             .title-section {
  //                 margin-bottom: 10px;
  //             }

  //             .details-item {
  //                 page-break-inside: avoid;
  //             }
  //         }
  //     </style>
  // </head>
  // <body>
  //     <div class="container">
  //         <div class="header">
  //             <div class="header-content">
  //                 <div class="header-logo">
  //                     ${
  //                       societyDetails.image
  //                         ? `<img src="${societyDetails.image}" alt="Society Logo" />`
  //                         : ""
  //                     }
  //                     <div class="header-logo-text">
  //                         <h1>${societyDetails.name}</h1>
  //                         <h4>${societyDetails.location}</h4>
  //                     </div>
  //                 </div>
  //                 <div class="header-booking-info">
  //                     <p><strong>Receipt No:</strong> ${booking.receipt_no}</p>
  //                     <p><strong>Booking No:</strong> ${
  //                       booking.booking_number
  //                     }</p>
  //                     <p><strong>Date:</strong> ${new Date(
  //                       booking.booking_date
  //                     ).toLocaleDateString("en-GB")}</p>
  //                 </div>
  //             </div>
  //         </div>

  //         <div class="content-wrapper">
  //             <div class="title-section">
  //                 <h2 class="title">Official Booking Confirmation</h2>
  //                 <p class="subtitle">Booked Plot Via Full Payment</p>
  //                 <span class="badge">✓ PAYMENT COMPLETED</span>
  //             </div>

  //             <div class="details-section">
  //                 <div class="details-column">
  //                     <h3>Customer Details</h3>
  //                     <p class="details-item"><strong>Name:</strong> ${
  //                       customer.name || "Unknown"
  //                     }</p>
  //                     <p class="details-item"><strong>Father Name:</strong> ${
  //                       customer.contactInfo?.father_name || "N/A"
  //                     }</p>
  //                     <p class="details-item"><strong>Phone:</strong> ${
  //                       customer.contactInfo?.phone || "N/A"
  //                     }</p>
  //                     <p class="details-item"><strong>Email:</strong> ${
  //                       customer.contactInfo?.email || "N/A"
  //                     }</p>
  //                     <p class="details-item"><strong>CNIC:</strong> ${
  //                       customer.contactInfo?.cnic || "N/A"
  //                     }</p>
  //                     <p class="details-item"><strong>Address:</strong> ${
  //                       customer.contactInfo?.address || "N/A"
  //                     }</p>
  //                 </div>

  //                 <div class="details-column">
  //                     <h3>Property Details</h3>
  //                     <p class="details-item"><strong>Plot Number:</strong> ${
  //                       booking.plot_id?.plot_number || "N/A"
  //                     }</p>
  //                     <p class="details-item"><strong>Plot Type:</strong> ${
  //                       booking.plot_id?.plot_type || "N/A"
  //                     }</p>
  //                     <p class="details-item"><strong>Category:</strong> ${
  //                       booking.plot_id?.category || "N/A"
  //                     }</p>
  //                     <p class="details-item"><strong>Plot Size:</strong> ${
  //                       booking.plot_id?.size || "N/A"
  //                     } Marla</p>
  //                     <p class="details-item amount-highlight"><strong>Total Amount:</strong> ${formatCurrency(
  //                       booking.total_amount
  //                     )}</p>
  //                 </div>
  //             </div>

  //             <div class="full-width-section">
  //                 <h3>Waris (Next of Kin) Details</h3>
  //                 <div class="waris-grid">
  //                     <p class="details-item"><strong>Name:</strong> ${
  //                       customer.contactInfo?.waris_name || "N/A"
  //                     }</p>
  //                     <p class="details-item"><strong>Relation:</strong> ${
  //                       customer.contactInfo?.waris_relation || "N/A"
  //                     }</p>
  //                     <p class="details-item"><strong>Father Name:</strong> ${
  //                       customer.contactInfo?.waris_father_name || "N/A"
  //                     }</p>
  //                     <p class="details-item"><strong>CNIC:</strong> ${
  //                       customer.contactInfo?.waris_cnic || "N/A"
  //                     }</p>
  //                     <p class="details-item"><strong>Phone:</strong> ${
  //                       customer.contactInfo?.waris_phone || "N/A"
  //                     }</p>
  //                     <p class="details-item"><strong>Address:</strong> ${
  //                       customer.contactInfo?.waris_address || "N/A"
  //                     }</p>
  //                 </div>
  //             </div>

  //             <div class="signature-section">
  //                 <div class="signature-column">
  //                     <p>Customer Signature</p>
  //                 </div>
  //                 <div class="signature-column">
  //                     <p>Authorized Signature</p>
  //                 </div>
  //             </div>

  //             <div class="terms-section">
  //                 <h3>Terms and Conditions</h3>
  //                 <ol>
  //                     <li>This booking is subject to final approval by ${
  //                       societyDetails.name
  //                     } management.</li>
  //                     <li>Payment plans are non-transferable and non-negotiable without prior written consent.</li>
  //                     <li>Any changes to the plot allocation require written consent from ${
  //                       societyDetails.name
  //                     }.</li>
  //                     <li>Default in payment may result in cancellation of booking and forfeiture of paid amounts.</li>
  //                     <li>All legal disputes will be settled in the jurisdiction of the city where the property is located.</li>
  //                     <li>The society reserves the right to modify terms with 30 days written notice to the customer.</li>
  //                 </ol>
  //             </div>
  //         </div>

  //         <div class="footer">
  //             <p>This is a computer-generated booking slip and does not require a physical signature.</p>
  //             <p>© ${new Date().getFullYear()} ${
  //       societyDetails.name
  //     }. All rights reserved.</p>
  //             <p>For queries, please contact our customer service department.</p>
  //         </div>
  //     </div>
  // </body>
  // </html>
  // `;

  //     const printWindow = window.open("", "_blank");

  //     if (!printWindow) {
  //       alert("Please allow pop-ups for this site to enable printing.");
  //       return;
  //     }

  //     printWindow.document.write(printContent);
  //     printWindow.document.close();

  //     printWindow.onload = function () {
  //       setTimeout(() => {
  //         printWindow.focus();
  //         printWindow.print();
  //         printWindow.onafterprint = function () {
  //           printWindow.close();
  //         };
  //       }, 250);
  //     };
  //   };

  const handlePrint = useReactToPrint({
    contentRef: componentRef
  })

  const handlePrintClick = booking => {
    const customer = booking.customer_id
    const societyDetails =
      booking.plot_id && booking.plot_id.society_id
        ? getSocietyDetails(booking.plot_id.society_id)
        : {
            name: 'Housing City',
            location: 'Main Branch, City Center',
            image: Logo
          }

    if (!customer || !customer.contactInfo) {
      console.error('Customer details are missing.')
      return
    }

    setPrintData({ booking, customer, societyDetails })

    // Trigger print after state update
    setTimeout(() => {
      handlePrint()
    }, 100)
  }

  const renderPageNumbers = () => {
    let pages = []
    const maxVisiblePages = 5

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total pages is less than or equal to max visible pages
      pages = Array.from({ length: totalPages }, (_, i) => i + 1)
    } else {
      // Calculate range of visible pages
      let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2))
      let endPage = startPage + maxVisiblePages - 1

      // Adjust if end page exceeds total pages
      if (endPage > totalPages) {
        endPage = totalPages
        startPage = Math.max(1, endPage - maxVisiblePages + 1)
      }

      pages = Array.from(
        { length: endPage - startPage + 1 },
        (_, i) => startPage + i
      )
    }

    return pages.map(pageNum => (
      <button
        key={pageNum}
        onClick={() => setCurrentPage(pageNum)}
        className={`flex items-center justify-center h-8 w-8 rounded-full text-sm font-medium transition-all duration-300 ${
          pageNum === currentPage
            ? `${theme.tailwind.bg13} text-white shadow-lg scale-110`
            : `bg-white/10 ${theme.tailwind.text9} hover:bg-slate-400 hover:text-white`
        }`}
      >
        {pageNum}
      </button>
    ))
  }

  const renderPagination = () => {
    if (totalPages <= 1) return null

    return (
      <div className='flex justify-center items-center gap-4 mt-6 mb-5 p-4'>
        <button
          onClick={handlePreviousPage}
          disabled={currentPage === 1}
          className={`flex items-center justify-center h-8 w-8 rounded-lg transition-all duration-300 ${
            currentPage === 1
              ? 'bg-slate-600 cursor-not-allowed'
              : `bg-slate-700 ${theme.tailwind.text9} hover:bg-slate-600`
          }`}
        >
          <ChevronLeft
            className={`w-4 h-4 ${
              currentPage === 1 ? 'text-slate-400' : `${theme.tailwind.text9}`
            }`}
          />
        </button>

        <div className='flex items-center gap-2'>
          {currentPage > 3 && totalPages > 5 && (
            <>
              <button
                onClick={() => setCurrentPage(1)}
                className={`flex items-center justify-center h-8 w-8 rounded-full text-sm font-medium bg-white/10 ${theme.tailwind.text9} hover:bg-slate-400 hover:text-white transition-all duration-300`}
              >
                1
              </button>
              <span className={`${theme.tailwind.text9}`}>...</span>
            </>
          )}

          {renderPageNumbers()}

          {currentPage < totalPages - 2 && totalPages > 5 && (
            <>
              <span className={`${theme.tailwind.text9}`}>...</span>
              <button
                onClick={() => setCurrentPage(totalPages)}
                className={`flex items-center justify-center h-8 w-8 rounded-full text-sm font-medium bg-white/10 ${theme.tailwind.text9} hover:bg-slate-400 hover:text-white transition-all duration-300`}
              >
                {totalPages}
              </button>
            </>
          )}
        </div>

        <button
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
          className={`flex items-center justify-center h-8 w-8 rounded-lg transition-all duration-300 ${
            currentPage === totalPages
              ? 'bg-slate-600 cursor-not-allowed'
              : `bg-slate-700 ${theme.tailwind.text9} hover:bg-slate-600`
          }`}
        >
          <ChevronRight
            className={`w-4 h-4 ${
              currentPage === totalPages
                ? 'text-slate-400'
                : `${theme.tailwind.text9}`
            }`}
          />
        </button>
      </div>
    )
  }
  const getCustomerName = customer => {
    return customer?.name || 'Unknown'
  }

  const formatCurrency = amount => {
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR'
    }).format(amount)
  }

  const getSocietyDetails = societyId => {
    const society = societies.find(s => s._id === societyId)
    return society
      ? {
          name: society.name,
          location: society.location,
          image: society.society_image
        }
      : {
          name: 'Housing City',
          location: 'Main Branch, City Center',
          image: Logo
        }
  }

  const handleDelete = booking => {
    if (isEmployee) return
    setSelectedBooking(booking)
    setDeleteDialogOpen(true)
  }

  const confirmDelete = async () => {
    if (isEmployee) return
    if (selectedBooking) {
      await dispatch(deleteBooking(selectedBooking._id))
      setDeleteDialogOpen(false)
      setSelectedBooking(null)
    }
  }

  const handleEdit = booking => {
    if (isEmployee) return
    setSelectedBooking(booking)
    setEditFormData({
      booking_number: booking.booking_number,
      total_amount: booking.total_amount,
      receipt_no: booking.receipt_no,
      booking_date: booking.booking_date,
      customer_username: booking.customer_id?.name || '',
      customer_id: booking.customer_id?._id || '',
      dealer_comission: booking.dealer_comission || 0,
      dealer_name: booking.dealer_name || '',
      dealer_phone: booking.dealer_phone || '',
      initial_payment: booking.initial_payment || 0,
      payment_mode: booking.payment_mode || 'Full',
      installment_years: booking.installment_years || 0,
      offertag: booking.offertag || '',
      dealer_commission_date: booking.dealer_commission_date || ''
    })
    setEditDialogOpen(true)
  }

  const handleUpdate = async () => {
    if (isEmployee) return
    if (selectedBooking && editFormData) {
      try {
        // Validate form data
        if (
          !editFormData.total_amount ||
          parseInt(editFormData.total_amount) <= 0
        ) {
          Swal.fire({
            title: 'Validation Error',
            text: 'Total amount must be greater than zero',
            icon: 'error'
          })
          return
        }

        // Payment mode-specific validations
        const isPaymentModeChanging =
          selectedBooking.payment_mode !== editFormData.payment_mode
        const totalAmount = parseInt(editFormData.total_amount)
        const initialPayment = parseInt(editFormData.initial_payment || 0)
        const isPriceChanging =
          totalAmount !== parseInt(selectedBooking.total_amount)
        const isInitialPaymentChanging =
          initialPayment !== parseInt(selectedBooking.initial_payment || 0)

        // Validate installment mode requirements
        if (editFormData.payment_mode === 'Installment') {
          const { years, months } = convertYearsToYearsAndMonths(
            editFormData.installment_years || 0
          )
          if (years === 0 && months === 0) {
            Swal.fire({
              title: 'Validation Error',
              text: 'At least one field (years or months) must be filled for installment payment mode',
              icon: 'error'
            })
            return
          }

          if (initialPayment >= totalAmount) {
            Swal.fire({
              title: 'Validation Error',
              text: 'Initial payment cannot be greater than or equal to total amount for installment mode',
              icon: 'error'
            })
            return
          }
        }

        // Check for price changes that would affect financials
        if (
          (isPriceChanging || isInitialPaymentChanging) &&
          (editFormData.payment_mode === 'Installment' ||
            (isPaymentModeChanging &&
              editFormData.payment_mode === 'Installment'))
        ) {
          const confirmPriceChange = await Swal.fire({
            title: 'Update Financial Records?',
            html: `<p>You are changing financial information on an existing booking:</p>
               <ul style="text-align: left; margin-top: 1em;">
                 ${
                   isPriceChanging
                     ? `<li>Total amount changing from ${parseInt(
                         selectedBooking.total_amount
                       ).toLocaleString()} 
                   to ${totalAmount.toLocaleString()}</li>`
                     : ''
                 }
                 ${
                   isInitialPaymentChanging
                     ? `<li>Initial payment changing from ${parseInt(
                         selectedBooking.initial_payment || 0
                       ).toLocaleString()} 
                   to ${initialPayment.toLocaleString()}</li>`
                     : ''
                 }
               </ul>
               <p style="margin-top: 1em;">This will update financial transactions and recalculate installment amounts.</p>
               <p>For ${editFormData.payment_mode} mode:</p>
               <ul style="text-align: left;">
                 <li>Total Amount: ${totalAmount.toLocaleString()}</li>
                 <li>Initial Payment: ${initialPayment.toLocaleString()}</li>
                 <li>Remaining Balance: ${(
                   totalAmount - initialPayment
                 ).toLocaleString()}</li>
                 ${
                   editFormData.payment_mode === 'Installment'
                     ? `<li>Installment Duration: ${formatInstallmentDuration(
                         editFormData.installment_years
                       )}</li>`
                     : ''
                 }
               </ul>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, Update Records',
            cancelButtonText: 'Cancel',
            reverseButtons: true
          })

          if (!confirmPriceChange.isConfirmed) {
            return
          }
        }

        // Changing from Full to Installment needs confirmation due to financial implications
        if (
          isPaymentModeChanging &&
          editFormData.payment_mode === 'Installment'
        ) {
          const confirmResult = await Swal.fire({
            title: 'Change to Installment Mode?',
            html: `<p>You are changing from Full Payment to Installment mode.</p>
               <p>This will:</p>
               <ul style="text-align: left; margin-top: 1em;">
                 <li>Create new installment records</li>
                 <li>Update financial transactions</li>
                 <li>Change plot status to "Installment"</li>
               </ul>
               <p style="margin-top: 1em;">Initial payment: ${initialPayment.toLocaleString()}</p>
               <p>Remaining for installments: ${(
                 totalAmount - initialPayment
               ).toLocaleString()}</p>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, Change to Installment',
            cancelButtonText: 'Cancel',
            reverseButtons: true
          })

          if (!confirmResult.isConfirmed) {
            return
          }
        }

        // Calculate remaining balance for installment mode
        let remainingBalance = 0
        if (editFormData.payment_mode === 'Installment') {
          remainingBalance = totalAmount - initialPayment
        }

        // Parse numeric values
        const bookingData = {
          ...editFormData,
          total_amount: totalAmount,
          initial_payment: initialPayment,
          dealer_comission: parseInt(editFormData.dealer_comission || 0),
          installment_years: parseFloat(editFormData.installment_years || 0),
          remaining_balance: remainingBalance,
          dealer_commission_date: editFormData.dealer_commission_date
        }

        // Show loading indicator for longer operations
        let loadingAlert
        if (
          isPaymentModeChanging ||
          isPriceChanging ||
          isInitialPaymentChanging
        ) {
          loadingAlert = Swal.fire({
            title: 'Processing...',
            html: 'Updating booking and financial records. This may take a moment.',
            allowOutsideClick: false,
            didOpen: () => {
              Swal.showLoading()
            }
          })
        }

        const response = await dispatch(
          updateBooking({
            bookingId: selectedBooking._id,
            bookingData: bookingData
          })
        ).unwrap()

        // Close loading indicator if it exists
        if (loadingAlert) {
          loadingAlert.close()
        }

        setEditDialogOpen(false)
        setSelectedBooking(null)

        // Refresh the bookings data after update
        dispatch(getAllBookings())

        Swal.fire({
          title: 'Success',
          html: generateSuccessMessage(
            isPaymentModeChanging,
            isPriceChanging,
            isInitialPaymentChanging,
            editFormData.payment_mode
          ),
          icon: 'success'
        })
      } catch (error) {
        console.error('Error updating booking:', error)
        Swal.fire({
          title: 'Error',
          text: error.message || 'Failed to update booking',
          icon: 'error'
        })
      }
    }
  }

  // Helper function to generate appropriate success message
  const generateSuccessMessage = (
    isPaymentModeChanging,
    isPriceChanging,
    isInitialPaymentChanging,
    paymentMode
  ) => {
    const messages = []

    if (isPaymentModeChanging) {
      messages.push(`Payment mode changed to ${paymentMode}.`)
    }

    if (isPriceChanging) {
      messages.push('Total amount updated.')
    }

    if (isInitialPaymentChanging) {
      messages.push('Initial payment updated.')
    }

    if (isPaymentModeChanging && paymentMode === 'Installment') {
      messages.push('New installments have been created.')
    }

    if (messages.length === 0) {
      return 'Booking updated successfully.'
    }

    return `Booking updated successfully.<br>${messages.join('<br>')}`
  }

  const formatDate = date => {
    if (!date) return 'N/A'
    try {
      const d = new Date(date)
      // Check if date is valid
      if (isNaN(d.getTime())) {
        return 'N/A'
      }
      const day = String(d.getDate()).padStart(2, '0')
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const year = d.getFullYear()
      return `${day}/${month}/${year}`
    } catch (error) {
      console.error('Error formatting date:', error, date)
      return 'N/A'
    }
  }
  const handleExport = () => {
    let filteredBookings = bookings
    console.log('Exporting bookings:', filteredBookings)
    // Determine which bookings to export based on export type
    if (exportType === 'filtered') {
      // Apply date range filter if preset is selected
      if (exportPreset !== 'custom') {
        const dateRanges = getDateRanges()
        const selectedRange = dateRanges[exportPreset]
        filteredBookings = filterBookingsByDateRange(
          filteredBookings,
          selectedRange.start,
          selectedRange.end,
          activeSociety?._id
        )
      } else if (exportStartDate && exportEndDate) {
        // Custom date range
        filteredBookings = filterBookingsByDateRange(
          filteredBookings,
          exportStartDate,
          exportEndDate,
          activeSociety?._id
        )
      }
    } else if (exportType === 'all') {
      // Filter all bookings for the active society
      filteredBookings = bookings.filter(
        booking =>
          booking.payment_mode === 'Full' &&
          booking.plot_id?.society_id === activeSociety?._id
      )
    }

    // Format and export data
    // IMPORTANT: Pass the society name as the third argument
    const formattedData = formatExportData(
      filteredBookings,
      activeSociety?._id,
      activeSociety?.name || 'Unknown Society'
    )

    exportToExcel(
      formattedData,
      activeSociety?.name || 'Unknown Society',
      exportType === 'all'
        ? 'all_full_payment_bookings'
        : 'filtered_full_payment_bookings'
    )

    // Close export dialog
    setExportDialogOpen(false)
  }

  // Export Dialog Component
  const renderExportDialog = () =>
    exportDialogOpen && (
      <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50'>
        <div
          className={`${theme.tailwind.bg14} rounded-2xl p-6 max-w-md w-full`}
        >
          <div className='flex justify-between items-center mb-4'>
            <h2 className={`text-lg font-semibold ${theme.tailwind.text9}`}>
              Export Bookings
            </h2>
            <button
              onClick={() => setExportDialogOpen(false)}
              className={`${theme.tailwind.text}`}
            >
              <X className='w-5 h-5' />
            </button>
          </div>

          <div className='space-y-4'>
            {/* Export Type Selection */}
            <div>
              <label className={`text-sm font-medium ${theme.tailwind.text9}`}>
                Export Type
              </label>
              <select
                value={exportType}
                onChange={e => setExportType(e.target.value)}
                className={`w-full p-2 ${theme.tailwind.bg16} border ${theme.tailwind.border11} rounded-lg mt-1 ${theme.tailwind.text9}`}
              >
                <option value='filtered'>Filtered Data</option>
                <option value='all'>All Data</option>
              </select>
            </div>

            {/* Date Range Selection (only for filtered export) */}
            {exportType === 'filtered' && (
              <>
                <div>
                  <label
                    className={`text-sm font-medium ${theme.tailwind.text9}`}
                  >
                    Date Range Preset
                  </label>
                  <select
                    value={exportPreset}
                    onChange={e => setExportPreset(e.target.value)}
                    className={`w-full p-2 ${theme.tailwind.bg16} border ${theme.tailwind.border11} rounded-lg mt-1 ${theme.tailwind.text9}`}
                  >
                    <option value='custom'>Custom Date Range</option>
                    <option value='today'>Today</option>
                    <option value='thisMonth'>This Month</option>
                    <option value='lastMonth'>Last Month</option>
                    <option value='thisYear'>This Year</option>
                    <option value='lastYear'>Last Year</option>
                  </select>
                </div>

                {exportPreset === 'custom' && (
                  <div className='grid grid-cols-2 gap-4'>
                    <div>
                      <label
                        className={`text-sm font-medium ${theme.tailwind.text9}`}
                      >
                        Start Date
                      </label>
                      <ReactDatePicker
                        selected={exportStartDate}
                        onChange={date => setExportStartDate(date)}
                        selectsStart
                        startDate={exportStartDate}
                        endDate={exportEndDate}
                        className={`w-full p-2 ${theme.tailwind.bg16} border ${theme.tailwind.border11} rounded-lg mt-1 ${theme.tailwind.text9}`}
                      />
                    </div>
                    <div>
                      <label
                        className={`text-sm font-medium ${theme.tailwind.text9}`}
                      >
                        End Date
                      </label>
                      <ReactDatePicker
                        selected={exportEndDate}
                        onChange={date => setExportEndDate(date)}
                        selectsEnd
                        startDate={exportStartDate}
                        endDate={exportEndDate}
                        minDate={exportStartDate}
                        className={`w-full p-2 ${theme.tailwind.bg16} border ${theme.tailwind.border11} rounded-lg mt-1 ${theme.tailwind.text9}`}
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <div className='flex justify-end space-x-4 mt-6'>
            <button
              onClick={() => setExportDialogOpen(false)}
              className={`px-4 py-2 border ${theme.tailwind.border11} rounded-lg ${theme.tailwind.text9} ${theme.tailwind.bgHover7}`}
            >
              Cancel
            </button>
            <button
              onClick={handleExport}
              className={`px-4 py-2 ${theme.tailwind.bg1} ${theme.tailwind.text} rounded-lg ${theme.tailwind.bgHover9}`}
              disabled={
                exportType === 'filtered' &&
                exportPreset === 'custom' &&
                (!exportStartDate || !exportEndDate)
              }
            >
              Export
            </button>
          </div>
        </div>
      </div>
    )

  const PrintableBooking = () => {
    if (!printData) return null

    const { booking, customer, societyDetails } = printData

    return (
      <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
        <div ref={componentRef}>
          <style>
            {`
          @media print {
            @page {
              size: A4;
              margin: 10mm;
            }
            body {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
          }
          
          .print-container {
            font-family: 'Inter', Arial, sans-serif;
            line-height: 1.4;
            color: #1a1a1a;
            background: white;
            width: 210mm;
          }
          
          .print-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            padding: 10px 15px;
            color: white;
            position: relative;
            overflow: hidden;
          }
          
          .print-header::before {
            content: '';
            position: absolute;
            top: -50%;
            right: -50%;
            width: 200%;
            height: 200%;
            background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
          }
          
          .print-header-content {
            position: relative;
            z-index: 1;
            display: flex;
            justify-content: space-between;
            align-items: center;
          }
          
          .print-header-logo {
            display: flex;
            align-items: center;
            gap: 10px;
          }
          
          .print-header-logo img {
            width: 45px;
            height: 45px;
            border-radius: 6px;
            border: 2px solid rgba(255,255,255,0.3);
            object-fit: cover;
          }
          
          .print-header-logo-text h1 {
            margin: 0;
            font-family: 'Playfair Display', serif;
            font-size: 20px;
            font-weight: 700;
            color: white;
          }
          
          .print-header-logo-text h4 {
            margin: 2px 0 0;
            font-size: 10px;
            color: rgba(255,255,255,0.9);
            font-weight: 300;
          }
          
          .print-header-booking-info {
            text-align: right;
            background: rgba(255,255,255,0.15);
            padding: 6px 12px;
            border-radius: 5px;
            backdrop-filter: blur(10px);
          }
          
          .print-header-booking-info p {
            margin: 2px 0;
            font-size: 10px;
            font-weight: 500;
          }
          
          .print-content-wrapper {
            padding: 15px 20px;
          }
          
          .print-title-section {
            text-align: center;
            margin-bottom: 12px;
          }
          
          .print-title {
            font-family: 'Playfair Display', serif;
            font-size: 18px;
            font-weight: 700;
            color: #667eea;
            margin-bottom: 4px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          
          .print-subtitle {
            font-size: 10px;
            color: #666;
            font-weight: 500;
            padding: 4px 12px;
            background: #f0f0f0;
            display: inline-block;
            border-radius: 12px;
          }
          
          .print-badge {
            display: inline-block;
            background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
            color: white;
            padding: 4px 12px;
            border-radius: 12px;
            font-size: 9px;
            font-weight: 600;
            margin-top: 5px;
          }
          
          .print-details-section {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 12px;
            margin-bottom: 12px;
          }
          
          .print-details-column {
            background: linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%);
            border: 2px solid #e0e7ff;
            border-radius: 6px;
            padding: 12px;
            position: relative;
            overflow: hidden;
          }
          
          .print-details-column::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 3px;
            height: 100%;
            background: linear-gradient(180deg, #667eea 0%, #764ba2 100%);
          }
          
          .print-details-column h3 {
            margin: 0 0 8px;
            font-size: 13px;
            font-weight: 700;
            color: #667eea;
            padding-bottom: 6px;
            border-bottom: 2px solid #e0e7ff;
            display: flex;
            align-items: center;
            gap: 6px;
          }
          
          .print-details-column h3::before {
            content: '●';
            color: #764ba2;
            font-size: 14px;
          }
          
          .print-details-item {
            background: white;
            border-left: 2px solid #e0e7ff;
            border-radius: 3px;
            padding: 5px 8px;
            margin-bottom: 5px;
            font-size: 10px;
            line-height: 1.3;
          }
          
          .print-details-item strong {
            color: #667eea;
            font-weight: 600;
            display: inline-block;
            min-width: 85px;
          }
          
          .print-full-width-section {
            background: linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%);
            border: 2px solid #e0e7ff;
            border-radius: 6px;
            padding: 12px;
            margin-bottom: 12px;
            position: relative;
            overflow: hidden;
          }
          
          .print-full-width-section::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 3px;
            height: 100%;
            background: linear-gradient(180deg, #667eea 0%, #764ba2 100%);
          }
          
          .print-full-width-section h3 {
            margin: 0 0 8px;
            font-size: 13px;
            font-weight: 700;
            color: #667eea;
            padding-bottom: 6px;
            border-bottom: 2px solid #e0e7ff;
            display: flex;
            align-items: center;
            gap: 6px;
          }
          
          .print-full-width-section h3::before {
            content: '●';
            color: #764ba2;
            font-size: 14px;
          }
          
          .print-waris-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 8px;
          }
          
          .print-signature-section {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 25px;
            margin: 15px 0 12px;
            padding: 0 12px;
          }
          
          .print-signature-column {
            text-align: center;
            position: relative;
            padding-top: 30px;
          }
          
          .print-signature-column::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            height: 1px;
            background: linear-gradient(90deg, transparent, #667eea, transparent);
          }
          
          .print-signature-column p {
            color: #667eea;
            font-weight: 600;
            font-size: 11px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          
          .print-terms-section {
            background: linear-gradient(135deg, #ffeaa7 0%, #fdcb6e 100%);
            border: 2px solid #f39c12;
            border-radius: 6px;
            padding: 12px;
            position: relative;
          }
          
          .print-terms-section::before {
            content: '⚠';
            position: absolute;
            top: 8px;
            right: 8px;
            font-size: 25px;
            opacity: 0.2;
          }
          
          .print-terms-section h3 {
            margin: 0 0 8px;
            font-size: 13px;
            font-weight: 700;
            color: #d35400;
            padding-bottom: 6px;
            border-bottom: 2px solid rgba(211, 84, 0, 0.3);
          }
          
          .print-terms-section ol {
            padding-left: 16px;
            line-height: 1.4;
            font-size: 9px;
            color: #7d4f1b;
            margin: 0;
          }
          
          .print-terms-section li {
            margin-bottom: 3px;
            font-weight: 500;
          }
          
          .print-footer {
            text-align: center;
            padding: 12px;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            margin-top: 12px;
          }
          
          .print-footer p {
            margin: 2px 0;
            font-size: 9px;
            opacity: 0.9;
          }
          
          .print-footer p:first-child {
            font-weight: 600;
            font-size: 10px;
          }
          
          .print-amount-highlight {
            background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%) !important;
            color: white !important;
            padding: 8px;
            border-radius: 5px;
            font-size: 12px;
            font-weight: 700;
            text-align: center;
            border: none !important;
          }
        `}
          </style>

          <div className='print-container'>
            <div className='print-header'>
              <div className='print-header-content'>
                <div className='print-header-logo'>
                  {societyDetails.image && (
                    <img src={societyDetails.image} alt='Society Logo' />
                  )}
                  <div className='print-header-logo-text'>
                    <h1>{societyDetails.name}</h1>
                    <h4>{societyDetails.location}</h4>
                  </div>
                </div>
                <div className='print-header-booking-info'>
                  <p>
                    <strong>Receipt No:</strong> {booking.receipt_no}
                  </p>
                  <p>
                    <strong>Booking No:</strong> {booking.booking_number}
                  </p>
                  <p>
                    <strong>Date:</strong>{' '}
                    {(() => {
                      try {
                        if (!booking.booking_date) return 'N/A'
                        const date = new Date(booking.booking_date)
                        return isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString('en-GB')
                      } catch {
                        return 'N/A'
                      }
                    })()}
                  </p>
                </div>
              </div>
            </div>

            <div className='print-content-wrapper'>
              <div className='print-title-section'>
                <h2 className='print-title'>Official Booking Confirmation</h2>
                <p className='print-subtitle'>Booked Plot Via Full Payment</p>
                <span className='print-badge'>✓ PAYMENT COMPLETED</span>
              </div>

              <div className='print-details-section'>
                <div className='print-details-column'>
                  <h3>Customer Details</h3>
                  <p className='print-details-item'>
                    <strong>Name:</strong> {customer.name || 'Unknown'}
                  </p>
                  <p className='print-details-item'>
                    <strong>Father Name:</strong>{' '}
                    {customer.contactInfo?.father_name || 'N/A'}
                  </p>
                  <p className='print-details-item'>
                    <strong>Phone:</strong>{' '}
                    {customer.contactInfo?.phone || 'N/A'}
                  </p>
                  <p className='print-details-item'>
                    <strong>Email:</strong>{' '}
                    {customer.contactInfo?.email || 'N/A'}
                  </p>
                  <p className='print-details-item'>
                    <strong>CNIC:</strong> {customer.contactInfo?.cnic || 'N/A'}
                  </p>
                  <p className='print-details-item'>
                    <strong>Address:</strong>{' '}
                    {customer.contactInfo?.address || 'N/A'}
                  </p>
                </div>

                <div className='print-details-column'>
                  <h3>Property Details</h3>
                  <p className='print-details-item'>
                    <strong>Plot Number:</strong>{' '}
                    {booking.plot_id?.plot_number || 'N/A'}
                  </p>
                  <p className='print-details-item'>
                    <strong>Plot Type:</strong>{' '}
                    {booking.plot_id?.plot_type || 'N/A'}
                  </p>
                  <p className='print-details-item'>
                    <strong>Category:</strong>{' '}
                    {booking.plot_id?.category || 'N/A'}
                  </p>
                  <p className='print-details-item'>
                    <strong>Plot Size:</strong> {booking.plot_id?.size || 'N/A'}{' '}
                    Marla
                  </p>
                  <p className='print-details-item print-amount-highlight'>
                    <strong>Total Amount:</strong>{' '}
                    {formatCurrency(booking.total_amount)}
                  </p>
                </div>
              </div>

              <div className='print-full-width-section'>
                <h3>Waris (Next of Kin) Details</h3>
                <div className='print-waris-grid'>
                  <p className='print-details-item'>
                    <strong>Name:</strong>{' '}
                    {customer.contactInfo?.waris_name || 'N/A'}
                  </p>
                  <p className='print-details-item'>
                    <strong>Relation:</strong>{' '}
                    {customer.contactInfo?.waris_relation || 'N/A'}
                  </p>
                  <p className='print-details-item'>
                    <strong>Father Name:</strong>{' '}
                    {customer.contactInfo?.waris_father_name || 'N/A'}
                  </p>
                  <p className='print-details-item'>
                    <strong>CNIC:</strong>{' '}
                    {customer.contactInfo?.waris_cnic || 'N/A'}
                  </p>
                  <p className='print-details-item'>
                    <strong>Phone:</strong>{' '}
                    {customer.contactInfo?.waris_phone || 'N/A'}
                  </p>
                  <p className='print-details-item'>
                    <strong>Address:</strong>{' '}
                    {customer.contactInfo?.waris_address || 'N/A'}
                  </p>
                </div>
              </div>

              <div className='print-signature-section'>
                <div className='print-signature-column'>
                  <p>Customer Signature</p>
                </div>
                <div className='print-signature-column'>
                  <p>Authorized Signature</p>
                </div>
              </div>

              <div className='print-terms-section'>
                <h3>Terms and Conditions</h3>
                <ol>
                  <li>
                    This booking is subject to final approval by{' '}
                    {societyDetails.name} management.
                  </li>
                  <li>
                    Payment plans are non-transferable and non-negotiable
                    without prior written consent.
                  </li>
                  <li>
                    Any changes to the plot allocation require written consent
                    from {societyDetails.name}.
                  </li>
                  <li>
                    Default in payment may result in cancellation of booking and
                    forfeiture of paid amounts.
                  </li>
                  <li>
                    All legal disputes will be settled in the jurisdiction of
                    the city where the property is located.
                  </li>
                  <li>
                    The society reserves the right to modify terms with 30 days
                    written notice to the customer.
                  </li>
                </ol>
              </div>
            </div>

            <div className='print-footer'>
              <p>
                This is a computer-generated booking slip and does not require a
                physical signature.
              </p>
              <p>
                © {new Date().getFullYear()} {societyDetails.name}. All rights
                reserved.
              </p>
              <p>
                For queries, please contact our customer service department.
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className={`min-h-full ${gradient} rounded-2xl shadow-[0_8px_32px_0_rgba(0,0,0,0.36)] p-8 font-inter ${theme.tailwind.text9} overflow-visible`}
    >
      <div className='max-w-full mx-auto space-y-8'>
        <div className='relative overflow-visible pb-2 md:pb-2'>
          <div className='absolute inset-0 bg-gradient-to-r from-emerald-500/10 via-teal-500/10 to-cyan-500/10 blur-3xl'></div>

          <div className='relative space-y-6'>
            <div className='flex flex-col md:flex-row md:items-start md:justify-between gap-4'>
              <div className='text-center md:text-left space-y-3'>
                <div className='flex flex-col sm:flex-row items-center justify-center md:justify-start gap-3'>
                  <div
                    className={`p-2.5 sm:p-3 ${theme.tailwind.bg1} rounded-xl shadow-lg`}
                  >
                    <FileText
                      className={`w-6 h-6 sm:w-8 sm:h-8 ${theme.tailwind.text}`}
                    />
                  </div>
                  <div className='space-y-1'>
                    <h1 className='text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black bg-gradient-to-r from-emerald-400 via-teal-500 to-cyan-600 bg-clip-text text-transparent drop-shadow-lg leading-tight'>
                      Full Payment Bookings
                    </h1>
                    <p
                      className={`text-xs sm:text-sm ${theme.tailwind.text10} font-medium`}
                    >
                      Complete payment transactions
                    </p>
                  </div>
                </div>

                {/* Society Badge */}
                <div className='flex justify-center md:justify-start'>
                  <div
                    className={`inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 ${theme.tailwind.bg14} border ${theme.tailwind.border11} rounded-full shadow-md`}
                  >
                    <MapPin
                      className={`w-4 h-4 sm:w-5 sm:h-5 ${theme.tailwind.text1}`}
                    />
                    <span
                      className={`text-sm sm:text-base font-semibold ${theme.tailwind.text9}`}
                    >
                      {activeSociety?.name || 'All Societies'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Filter Toggle Button - Top Right */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center justify-center gap-2 ${theme.tailwind.bg1} px-4 py-2.5 sm:py-3 rounded-xl ${theme.tailwind.bgHover9} transition-all ${theme.tailwind.text} shadow-lg font-semibold text-sm self-center md:self-start`}
              >
                <Filter className='w-5 h-5' />
                <span>{showFilters ? 'Hide' : 'Show'} Filters</span>
                <ChevronDown
                  className={`w-4 h-4 transition-transform duration-300 ${
                    showFilters ? 'rotate-180' : ''
                  }`}
                />
              </button>
            </div>

            {/* Search and Filters Section - Collapsible with Animation */}
            <div
              className={`transition-all duration-500 ease-in-out overflow-hidden ${
                showFilters ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
              }`}
            >
              <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 relative'>
                {/* Booking/Customer Search */}
                <div className='relative sm:col-span-1'>
                  <input
                    type='text'
                    placeholder='Search booking or customer...'
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    className={`w-full pl-10 pr-4 py-2.5 sm:py-3 text-sm ${theme.tailwind.bg14} border ${theme.tailwind.border11} rounded-xl focus:ring-2 ${theme.tailwind.focusRing2} transition-all ${theme.tailwind.text9} ${theme.tailwind.placeHolder1} shadow-md`}
                  />
                  <Search
                    className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 ${theme.tailwind.text10}`}
                  />
                </div>

                {/* Plot Number Search */}
                <div className='relative sm:col-span-1'>
                  <input
                    type='text'
                    placeholder='Search plot number...'
                    value={plotSearchTerm}
                    onChange={e => setPlotSearchTerm(e.target.value)}
                    className={`w-full pl-10 pr-4 py-2.5 sm:py-3 text-sm ${theme.tailwind.bg14} border ${theme.tailwind.border11} rounded-xl focus:ring-2 ${theme.tailwind.focusRing2} transition-all ${theme.tailwind.text9} ${theme.tailwind.placeHolder1} shadow-md`}
                  />
                  <MapPin
                    className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 ${theme.tailwind.text10}`}
                  />
                </div>

                {/* Sort Dropdown */}
                <div className='relative group sm:col-span-1 z-50'>
                  <button
                    className={`w-full ${theme.tailwind.bg14} border ${theme.tailwind.border11} px-4 py-2.5 sm:py-3 rounded-xl flex items-center justify-center gap-2 ${theme.tailwind.bgHover7} transition-all ${theme.tailwind.text9} shadow-md text-sm`}
                  >
                    <Filter
                      className={`w-4 h-4 sm:w-5 sm:h-5 ${theme.tailwind.text10}`}
                    />
                    <span className='hidden sm:inline'>Sort</span>
                    <ChevronDown className='w-4 h-4' />
                  </button>

                  <div
                    className={`absolute left-0 sm:right-0 sm:left-auto mt-2 w-full sm:w-56 ${theme.tailwind.bg14} border ${theme.tailwind.border11} rounded-xl shadow-2xl hidden group-hover:block z-50 p-2`}
                  >
                    {[
                      { label: 'Latest', value: 'latest' },
                      { label: 'Amount: Low to High', value: 'amount-asc' },
                      { label: 'Amount: High to Low', value: 'amount-desc' }
                    ].map(option => (
                      <button
                        key={option.value}
                        onClick={() => setSortOption(option.value)}
                        className={`w-full text-left px-3 py-2 rounded-lg ${
                          theme.tailwind.bgHover7
                        } transition-all text-sm ${
                          sortOption === option.value
                            ? `${theme.tailwind.bg1} ${theme.tailwind.text9}`
                            : `${theme.tailwind.text9}`
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Export Button */}
                <button
                  onClick={() => setExportDialogOpen(true)}
                  className={`flex items-center justify-center gap-2 ${theme.tailwind.bg1} px-4 py-2.5 sm:py-3 rounded-xl ${theme.tailwind.bgHover9} transition-all ${theme.tailwind.text} shadow-lg font-semibold text-sm sm:col-span-1`}
                >
                  <Download className='w-4 h-4 sm:w-5 sm:h-5' />
                  <span>Export</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div
          className={`${theme.tailwind.bg6} border ${theme.tailwind.border11} rounded-2xl overflow-hidden shadow-2xl backdrop-blur-xl`}
        >
          {paginatedBookings.length > 0 ? (
            <div className='overflow-x-auto'>
              <table className='w-full'>
                <thead
                  className={`${theme.tailwind.bg15} backdrop-blur-sm border-b ${theme.tailwind.border11}`}
                >
                  <tr>
                    {[
                      'Booking',
                      'Customer',
                      'Amount',
                      'Dealer Commission Date',
                      'Actions'
                    ].map(header => (
                      <th
                        key={header}
                        className={`px-6 py-4 text-left text-xs font-semibold ${theme.tailwind.text9} uppercase tracking-wider`}
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paginatedBookings.map(booking => (
                    <tr
                      key={booking._id}
                      className={`border-b ${theme.tailwind.border12} ${theme.tailwind.bgHover8} transition-all duration-300`}
                    >
                      <td className='px-6 py-4'>
                        <div className='flex items-center'>
                          <div
                            className={`${theme.tailwind.bg1} p-3 rounded-xl mr-4`}
                          >
                            <FileText
                              className={`w-4 h-4 ${theme.tailwind.text}`}
                            />
                          </div>
                          <div className='relative'>
                            <p
                              className={`${theme.tailwind.text9} font-semibold `}
                            >
                              Booking no.{booking.booking_number}
                            </p>

                            <p className={`text-xs ${theme.tailwind.text10} `}>
                              {formatDate(booking.booking_date)}
                            </p>
                            <p className={`text-xs ${theme.tailwind.text10} `}>
                              Receipt no.{booking.receipt_no}
                            </p>

                            {booking.offertag && (
                              <div className='relative -bottom-1 -right-0'>
                                <div className='bg-red-600 text-white text-xs font-semibold px-3 py-1 rounded-full shadow-lg flex items-center space-x-1'>
                                  <span>{booking.offertag}</span>
                                  <svg
                                    xmlns='http://www.w3.org/2000/svg'
                                    className='h-3 w-3'
                                    viewBox='0 0 20 20'
                                    fill='currentColor'
                                  >
                                    <path
                                      fillRule='evenodd'
                                      d='M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z'
                                      clipRule='evenodd'
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>

                      <td className='px-6 py-4'>
                        <div>
                          <p
                            className={`${theme.tailwind.text9} text-lg font-semibold`}
                          >
                            {getCustomerName(booking.customer_id)}
                          </p>
                          <p className={`text-xs ${theme.tailwind.text10}`}>
                            Full Payment
                          </p>
                          <p
                            className={`${theme.tailwind.text9} font-bold mr-2`}
                          >
                            Plot no. {booking.plot_id?.plot_number}
                          </p>
                        </div>
                      </td>
                      <td className='px-6 py-4'>
                        <div className='flex items-center'>
                          <p
                            className={`${theme.tailwind.text9} font-bold mr-2`}
                          >
                            {formatCurrency(booking.total_amount)}
                          </p>
                          <span className='bg-green-500/20 text-green-400 px-2 py-1 rounded-full text-xs'>
                            Paid
                          </span>
                        </div>
                      </td>
                      <td className='px-6 py-4'>
                        {booking.dealer_commission_date
                          ? formatDate(booking.dealer_commission_date)
                          : '-'}
                      </td>
                      <td className='px-6 py-4'>
                        <div className='flex items-center space-x-2'>
                          <button
                            onClick={() => {
                              handlePrintClick(booking)
                            }}
                            className={`${theme.tailwind.bg1} ${theme.tailwind.text} p-2 rounded-xl ${theme.tailwind.bgHover9} transition-all`}
                          >
                            <Printer className='w-5 h-5' />
                          </button>
                          {!isEmployee && (
                            <>
                              <button
                                onClick={() => handleEdit(booking)}
                                className='bg-blue-500/20 text-blue-400 p-2 rounded-xl hover:bg-blue-500/40 transition-all'
                              >
                                <Edit className='w-5 h-5' />
                              </button>
                              <button
                                onClick={() => handleDelete(booking)}
                                className='bg-red-500/20 text-red-400 p-2 rounded-xl hover:bg-red-500/40 transition-all'
                              >
                                <Trash2 className='w-5 h-5' />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className='flex flex-col items-center justify-center py-20 space-y-6'>
              <div
                className={`${theme.tailwind.bg14} p-6 rounded-full animate-pulse`}
              >
                <MapPin className={`w-12 h-12 ${theme.tailwind.text10}`} />
              </div>
              <p
                className={`text-3xl font-bold bg-clip-text text-transparent ${theme.tailwind.text8}`}
              >
                No Bookings Found
              </p>
              <p className={`${theme.tailwind.text10} text-center max-w-md`}>
                There are no full payment bookings for the selected society. Try
                adjusting your filters or selecting a different society.
              </p>
            </div>
          )}
        </div>
      </div>
      {renderPagination()}

      {deleteDialogOpen && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50'>
          <div
            className={`${theme.tailwind.bg14} rounded-2xl p-6 max-w-md w-full`}
          >
            <div className='flex justify-between items-center mb-4'>
              <h2 className={`text-lg font-semibold ${theme.tailwind.text9}`}>
                Confirm Deletion
              </h2>
              <button
                onClick={() => setDeleteDialogOpen(false)}
                className={`${theme.tailwind.text}`}
              >
                <X className='w-5 h-5' />
              </button>
            </div>
            <p className={`${theme.tailwind.text9} mb-6`}>
              Are you sure you want to delete booking{' '}
              {selectedBooking?.booking_number}?
            </p>
            <div className='flex justify-end space-x-4'>
              <button
                onClick={() => setDeleteDialogOpen(false)}
                className={`px-4 py-2 border ${theme.tailwind.border11} rounded-lg ${theme.tailwind.text9} ${theme.tailwind.bgHover7}`}
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className='px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/40'
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {editDialogOpen && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50'>
          <div
            className={`${theme.tailwind.bg14} rounded-2xl p-6 max-w-md w-full overflow-y-auto max-h-[90vh]`}
          >
            <div className='flex justify-between items-center mb-4'>
              <h2 className={`text-lg font-semibold ${theme.tailwind.text9}`}>
                Edit Booking
              </h2>
              <button
                onClick={() => setEditDialogOpen(false)}
                className={`${theme.tailwind.text}`}
              >
                <X className='w-5 h-5' />
              </button>
            </div>
            <div className='space-y-4'>
              <div>
                <label
                  className={`text-sm font-medium ${theme.tailwind.text9}`}
                >
                  Customer Username
                </label>
                <div className='relative'>
                  <input
                    type='text'
                    value={editFormData.customer_username || ''}
                    onChange={e =>
                      setEditFormData({
                        ...editFormData,
                        customer_username: e.target.value
                      })
                    }
                    readOnly
                    className={`w-full p-2 ${theme.tailwind.bg16} border ${theme.tailwind.border11} rounded-lg mt-1 ${theme.tailwind.text9} pr-8`}
                  />
                  <Lock
                    className={`absolute top-1/2 right-3 transform -translate-y-1/2 ${theme.tailwind.text9}`}
                  />
                </div>
              </div>

              <div>
                <label
                  className={`text-sm font-medium ${theme.tailwind.text9}`}
                >
                  Booking Number
                </label>
                <input
                  type='text'
                  value={editFormData.booking_number || ''}
                  onChange={e =>
                    setEditFormData({
                      ...editFormData,
                      booking_number: e.target.value
                    })
                  }
                  className={`w-full p-2 ${theme.tailwind.bg16} border ${theme.tailwind.border11} rounded-lg mt-1 ${theme.tailwind.text9}`}
                />
              </div>

              <div>
                <label
                  className={`text-sm font-medium ${theme.tailwind.text9}`}
                >
                  Payment Mode
                </label>
                <select
                  value={editFormData.payment_mode || 'Full'}
                  onChange={e =>
                    setEditFormData({
                      ...editFormData,
                      payment_mode: e.target.value
                    })
                  }
                  className={`w-full p-2 ${theme.tailwind.bg16} border ${theme.tailwind.border11} rounded-lg mt-1 ${theme.tailwind.text9}`}
                >
                  <option value='Full'>Full Payment</option>
                  <option value='Installment'>Installment</option>
                </select>
              </div>

              <div>
                <label
                  className={`text-sm font-medium ${theme.tailwind.text9}`}
                >
                  Total Amount
                </label>
                <input
                  type='text'
                  value={
                    editFormData.total_amount
                      ? parseInt(editFormData.total_amount, 10).toLocaleString(
                          'en-US'
                        )
                      : ''
                  }
                  onChange={e => {
                    const rawValue = e.target.value.replace(/,/g, '') // Remove commas
                    if (!isNaN(rawValue)) {
                      setEditFormData({
                        ...editFormData,
                        total_amount: rawValue // Store raw numeric value
                      })
                    }
                  }}
                  className={`w-full p-2 ${theme.tailwind.bg16} border ${theme.tailwind.border11} rounded-lg mt-1 ${theme.tailwind.text9}`}
                />
              </div>

              <div>
                <label
                  className={`text-sm font-medium ${theme.tailwind.text9}`}
                >
                  Initial Payment
                </label>
                <input
                  type='text'
                  value={
                    editFormData.initial_payment
                      ? parseInt(
                          editFormData.initial_payment,
                          10
                        ).toLocaleString('en-US')
                      : '0'
                  }
                  onChange={e => {
                    const rawValue = e.target.value.replace(/,/g, '') // Remove commas
                    if (!isNaN(rawValue)) {
                      setEditFormData({
                        ...editFormData,
                        initial_payment: rawValue // Store raw numeric value
                      })
                    }
                  }}
                  className={`w-full p-2 ${theme.tailwind.bg16} border ${theme.tailwind.border11} rounded-lg mt-1 ${theme.tailwind.text9}`}
                />
              </div>

              {editFormData.payment_mode === 'Installment' && (
                <div className='flex space-x-4'>
                  <div className='flex-1'>
                    <label
                      className={`text-sm font-medium ${theme.tailwind.text9}`}
                    >
                      Installment Years
                    </label>
                    <select
                      value={
                        convertYearsToYearsAndMonths(
                          editFormData.installment_years || 0
                        ).years
                      }
                      onChange={e => {
                        const currentMonths = convertYearsToYearsAndMonths(
                          editFormData.installment_years || 0
                        ).months
                        const newYears = parseInt(e.target.value)
                        const newTotalYears = newYears + currentMonths / 12
                        setEditFormData({
                          ...editFormData,
                          installment_years: newTotalYears
                        })
                      }}
                      className={`w-full p-2 ${theme.tailwind.bg16} border ${theme.tailwind.border11} rounded-lg mt-1 ${theme.tailwind.text9}`}
                    >
                      <option value={0}>0 Years</option>
                      <option value={1}>1 Year</option>
                      <option value={2}>2 Years</option>
                      <option value={3}>3 Years</option>
                      <option value={4}>4 Years</option>
                      <option value={5}>5 Years</option>
                      <option value={6}>6 Years</option>
                      <option value={7}>7 Years</option>
                      <option value={8}>8 Years</option>
                      <option value={9}>9 Years</option>
                      <option value={10}>10 Years</option>
                    </select>
                  </div>

                  <div className='flex-1'>
                    <label
                      className={`text-sm font-medium ${theme.tailwind.text9}`}
                    >
                      Installment Months
                    </label>
                    <select
                      value={
                        convertYearsToYearsAndMonths(
                          editFormData.installment_years || 0
                        ).months
                      }
                      onChange={e => {
                        const currentYears = convertYearsToYearsAndMonths(
                          editFormData.installment_years || 0
                        ).years
                        const newMonths = parseInt(e.target.value)
                        const newTotalYears = currentYears + newMonths / 12
                        setEditFormData({
                          ...editFormData,
                          installment_years: newTotalYears
                        })
                      }}
                      className={`w-full p-2 ${theme.tailwind.bg16} border ${theme.tailwind.border11} rounded-lg mt-1 ${theme.tailwind.text9}`}
                    >
                      <option value={0}>0 Months</option>
                      <option value={1}>1 Month</option>
                      <option value={2}>2 Months</option>
                      <option value={3}>3 Months</option>
                      <option value={4}>4 Months</option>
                      <option value={5}>5 Months</option>
                      <option value={6}>6 Months</option>
                      <option value={7}>7 Months</option>
                      <option value={8}>8 Months</option>
                      <option value={9}>9 Months</option>
                      <option value={10}>10 Months</option>
                      <option value={11}>11 Months</option>
                    </select>
                  </div>
                </div>
              )}

              <div>
                <label
                  className={`text-sm font-medium ${theme.tailwind.text9}`}
                >
                  Receipt Number
                </label>
                <input
                  type='text'
                  value={editFormData.receipt_no || ''}
                  onChange={e =>
                    setEditFormData({
                      ...editFormData,
                      receipt_no: e.target.value
                    })
                  }
                  className={`w-full p-2 ${theme.tailwind.bg16} border ${theme.tailwind.border11} rounded-lg mt-1 ${theme.tailwind.text9}`}
                />
              </div>

              <div>
                <label
                  className={`text-sm font-medium ${theme.tailwind.text9}`}
                >
                  Offer Tag
                </label>
                <input
                  type='text'
                  value={editFormData.offertag || ''}
                  onChange={e =>
                    setEditFormData({
                      ...editFormData,
                      offertag: e.target.value
                    })
                  }
                  className={`w-full p-2 ${theme.tailwind.bg16} border ${theme.tailwind.border11} rounded-lg mt-1 ${theme.tailwind.text9}`}
                />
              </div>

              <div>
                <label
                  className={`text-sm font-medium ${theme.tailwind.text9} block mb-2`}
                >
                  Booking Date
                </label>
                <ReactDatePicker
                  selected={
                    editFormData.booking_date
                      ? new Date(editFormData.booking_date)
                      : null
                  }
                  onChange={date =>
                    setEditFormData({
                      ...editFormData,
                      booking_date: date ? date.toISOString().split('T')[0] : ''
                    })
                  }
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode='select'
                  wrapperClassName='w-full'
                  dateFormat='dd/MM/yyyy'
                  className={`w-full p-2 ${theme.tailwind.bg16} border ${theme.tailwind.border11} rounded-lg mt-1 ${theme.tailwind.text9}`}
                />
              </div>

              <div>
                <label
                  className={`text-sm font-medium ${theme.tailwind.text9}`}
                >
                  Dealer Commission
                </label>
                <input
                  type='text'
                  value={
                    editFormData.dealer_comission
                      ? parseInt(
                          editFormData.dealer_comission,
                          10
                        ).toLocaleString('en-US')
                      : '0'
                  }
                  onChange={e => {
                    const rawValue = e.target.value.replace(/,/g, '') // Remove commas
                    if (!isNaN(rawValue)) {
                      setEditFormData({
                        ...editFormData,
                        dealer_comission: rawValue // Store raw numeric value
                      })
                    }
                  }}
                  className={`w-full p-2 ${theme.tailwind.bg16} border ${theme.tailwind.border11} rounded-lg mt-1 ${theme.tailwind.text9}`}
                />
              </div>

              <div>
                <label
                  className={`text-sm font-medium ${theme.tailwind.text9}`}
                >
                  Dealer Name
                </label>
                <input
                  type='text'
                  value={editFormData.dealer_name || ''}
                  onChange={e =>
                    setEditFormData({
                      ...editFormData,
                      dealer_name: e.target.value
                    })
                  }
                  className={`w-full p-2 ${theme.tailwind.bg16} border ${theme.tailwind.border11} rounded-lg mt-1 ${theme.tailwind.text9}`}
                />
              </div>

              <div>
                <label
                  className={`text-sm font-medium ${theme.tailwind.text9}`}
                >
                  Dealer Phone
                </label>
                <input
                  type='text'
                  value={editFormData.dealer_phone || ''}
                  onChange={e =>
                    setEditFormData({
                      ...editFormData,
                      dealer_phone: e.target.value
                    })
                  }
                  className={`w-full p-2 ${theme.tailwind.bg16} border ${theme.tailwind.border11} rounded-lg mt-1 ${theme.tailwind.text9}`}
                />
              </div>

              <div>
                <label
                  className={`text-sm font-medium ${theme.tailwind.text9}`}
                >
                  Dealer Commission Date
                </label>
                <ReactDatePicker
                  selected={
                    editFormData.dealer_commission_date
                      ? new Date(editFormData.dealer_commission_date)
                      : null
                  }
                  onChange={date =>
                    setEditFormData({
                      ...editFormData,
                      dealer_commission_date: date
                        ? date.toISOString().split('T')[0]
                        : ''
                    })
                  }
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode='select'
                  wrapperClassName='w-full'
                  dateFormat='dd/MM/yyyy'
                  className={`w-full p-2 ${theme.tailwind.bg16} border ${theme.tailwind.border11} rounded-lg mt-1 ${theme.tailwind.text9}`}
                />
              </div>
            </div>
            <div className='flex justify-end space-x-4 mt-6'>
              <button
                onClick={() => setEditDialogOpen(false)}
                className={`px-4 py-2 border ${theme.tailwind.border11} rounded-lg ${theme.tailwind.text9} ${theme.tailwind.bgHover7}`}
              >
                Cancel
              </button>
              <button
                onClick={handleUpdate}
                className={`px-4 py-2 ${theme.tailwind.bg1} ${theme.tailwind.text} rounded-lg ${theme.tailwind.bgHover9}`}
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}

      {renderExportDialog()}
      <PrintableBooking />
    </div>
  )
}

export default FullPaymentBookings
